import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { subscriptionAdminApi } from '../../subscriptionAdmin/api';
import { hasPermission } from '../../subscriptionAdmin/permissions';

const PRIMARY_ACTIONS = new Set(['submit', 'approve', 'send-for-payment', 'activate', 'activate-offline', 'record-payment']);
const DESTRUCTIVE_ACTIONS = new Set(['terminate', 'cancel', 'suspend']);

export const CONTRACT_LIFECYCLE = [
  {
    key: 'submit',
    label: 'Submit',
    statuses: ['draft'],
    fn: (id) => subscriptionAdminApi.submitContract(id),
  },
  {
    key: 'approve',
    label: 'Approve',
    statuses: ['pending_approval'],
    fn: (id) => subscriptionAdminApi.approveContract(id),
  },
  {
    key: 'send-for-payment',
    label: 'Send for payment',
    statuses: ['approved'],
    fn: (id) => subscriptionAdminApi.sendContractForPayment(id),
  },
  {
    key: 'activate',
    label: 'Activate',
    statuses: ['approved', 'pending_payment'],
    fn: (id) => subscriptionAdminApi.activateContract(id),
    when: (contract) => contract?.billing?.paymentChannel !== 'offline',
  },
  {
    key: 'activate-offline',
    label: 'Activate offline',
    statuses: ['approved', 'pending_payment'],
    permission: 'contracts:activate_offline',
    dialog: 'offlineActivate',
    when: (contract) => contract?.billing?.paymentChannel === 'offline',
  },
  {
    key: 'record-payment',
    label: 'Record payment',
    statuses: ['pending_payment', 'active', 'renewal_pending'],
    permission: 'payments:record_manual',
    dialog: 'manualPayment',
    when: (contract) => contract?.billing?.paymentChannel === 'offline',
  },
  {
    key: 'run-billing',
    label: 'Run billing',
    statuses: ['active'],
    fn: (id) => subscriptionAdminApi.runBilling(id),
  },
  {
    key: 'suspend',
    label: 'Suspend',
    statuses: ['active'],
    fn: (id) => subscriptionAdminApi.suspendContract(id, { note: 'Suspended from admin UI' }),
  },
  {
    key: 'resume',
    label: 'Resume',
    statuses: ['suspended'],
    fn: (id) => subscriptionAdminApi.resumeContract(id),
  },
  {
    key: 'renew',
    label: 'Renew',
    statuses: ['active', 'renewal_pending', 'expired'],
    fn: (id) => subscriptionAdminApi.renewContract(id),
  },
  {
    key: 'terminate',
    label: 'Terminate',
    statuses: ['active', 'suspended', 'expired'],
    fn: (id) => subscriptionAdminApi.terminateContract(id),
  },
  {
    key: 'cancel',
    label: 'Cancel',
    statuses: ['draft', 'pending_approval', 'approved', 'pending_payment'],
    fn: (id) => subscriptionAdminApi.cancelContract(id),
  },
];

function contractRowId(contract) {
  return String(contract?._id || contract?.id || '');
}

function isActionVisible(action, contract) {
  if (!action.statuses.includes(contract.status)) return false;
  if (action.when && !action.when(contract)) return false;
  if (action.permission && !hasPermission(action.permission)) return false;
  return true;
}

function actionButtonProps(actionKey) {
  if (PRIMARY_ACTIONS.has(actionKey)) {
    return { variant: 'contained', color: 'primary', disableElevation: true };
  }
  if (DESTRUCTIVE_ACTIONS.has(actionKey)) {
    return { variant: 'outlined', color: 'error' };
  }
  return { variant: 'outlined', color: 'inherit' };
}

export default function ContractLifecycleBar({
  contract,
  onAction,
  disabled,
  pendingByContract = {},
  layout = 'inline',
}) {
  if (!contract) return null;

  const rowId = contractRowId(contract);
  const actions = CONTRACT_LIFECYCLE.filter((a) => isActionVisible(a, contract));
  const activeActionKey = pendingByContract[rowId];
  const rowBusy = !!activeActionKey;

  if (!actions.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No actions available
      </Typography>
    );
  }

  const buttonSx = layout === 'card'
    ? {
        flex: '1 1 calc(50% - 6px)',
        minWidth: 120,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        py: 0.75,
      }
    : { textTransform: 'none' };

  const content = actions.map((a) => {
    const isActive = activeActionKey === a.key;
    const props = actionButtonProps(a.key);

    return (
      <Button
        key={a.key}
        size="small"
        {...props}
        disabled={disabled || rowBusy}
        sx={buttonSx}
        onClick={() => {
          if (rowBusy) return;
          onAction(a);
        }}
      >
        {isActive ? 'Working…' : a.label}
      </Button>
    );
  });

  if (layout === 'card') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
      {content}
    </Stack>
  );
}
